<template>
    <hts-drawer
        v-model="visible"
        :rules="rules"
        :submit="handleSubmit"
        title="Cập nhật thông tin công ty"
        no-reset
        size="large"
        @close="close">
        <hts-card
            title="Thông tin công ty"
            :loading="loading">
            <hts-form-input
                v-model="companyData.name"
                :label="$t('labels.companyName')"
                name="companyName"
                required
                prop="name" />
            <div class="grid gap-4 grid-cols-1 sm:grid-cols-2">
                <hts-form-input
                    v-model="companyData.abbreviationName"
                    :label="$t('labels.abbreviationName')"
                    name="abbreviationName"
                    required
                    prop="abbreviationName" />
                <hts-form-input
                    v-model="companyData.taxNumber"
                    :label="$t('labels.taxNumber')"
                    name="taxNumber"
                    required
                    prop="taxNumber" />
                <hts-form-input
                    v-model="companyData.email"
                    :label="$t('labels.email')"
                    name="email"
                    required
                    prop="email" />
                <hts-form-phone
                    v-model="companyData.phone"
                    :label="$t('labels.phone')"
                    prop="phone"
                    required
                    name="phone"
                    @updateCode="(val) => (companyData.phone.code = val)" />

                <hts-form-input
                    v-model="companyData.website"
                    :label="$t('labels.website')"
                    name="website"
                    prop="website" />

                <hts-form-input
                    v-model="companyData.fax"
                    :label="$t('labels.fax')"
                    name="fax"
                    prop="fax" />
                <hts-form-input
                    v-model="companyData.address"
                    :label="$t('labels.address')"
                    name="address"
                    prop="address"
                    required
                    @change="setDefaultDataPostionByAddress" />
                <hts-form-input
                    v-model="companyData.locationName"
                    :label="$t('labels.location')"
                    name="location"
                    prop="location"
                    autocomplete="off"
                    required
                    @click="openModalLocation" />
            </div>
        </hts-card>

        <hts-modal
            :show="showLocation"
            :title="$t('labels.locationInformation')"
            @hide="showLocation = false">
            <template #custom>
                <hts-map-autocomplete
                    :label="$t('labels.findPosition')"
                    :required="true"
                    :placeholder="$t('labels.nodeModule.search')"
                    @get-place-detail="(data:any)=>handleSetPosition(data)" />
                <hts-form-input
                    v-model="companyData.locationName"
                    :label="$t('labels.location')"
                    :suffix="false"
                    required
                    disabled
                    name="location"
                    prop="location" />
                <hts-map-node
                    :center-position="companyData.location"
                    center-marker
                    @click="getLocationMap" />
            </template>
        </hts-modal>
    </hts-drawer>
</template>
<script setup lang="ts">
const visible = ref(false)
const loading = ref(false)
const showLocation = ref(false)
const emits = defineEmits(['update'])

const store = $store()

const companyData = ref({
  name: '',
  abbreviationName: '',
  taxNumber: '',
  email: '',
  phone: {
    countryCode: '',
    phone: '',
    code: ''
  },
  website: '',
  fax: '',
  address: '',
  locationName: '',
  location: {
    lat: 10.7561542,
    lng: 106.6896784
  }
})

const rulesDefault = {
  name: 'required',
  abbreviationName: 'required',
  taxNumber: 'required',
  website: 'regexUrl',
  email: 'regexEmail',
  phone: 'required',
  markup: 'min_value:0',
  location: 'required'
}

const rules = computed(() => {
  const result = $lodash.cloneDeep(rulesDefault)
  if (companyData.value.locationName && companyData.value.locationName.search('Việt Nam') !== -1) {
    result.taxNumber = 'required|regexTaxNumber'
  }
  return result
})

const open = () => {
  visible.value = true
  getCompanyInfo()
}

const getCompanyInfo = async () => {
  loading.value = true
  const company = await $api($url.company.getHoldingCompany)
  const data: any = $lodash.get(company, 'data.value')

  if (data.success) {
    if (!data.data.phone) {
      data.data.phone = {
        countryCode: '',
        phone: '',
        code: ''
      }
    }
    companyData.value = data.data
  }
  loading.value = false
}

const setDefaultDataPostionByAddress = $lodash.debounce(() => {
  const geocoder = new google.maps.Geocoder()
  const address = companyData.value.address

  if (!address) {
    companyData.value.locationName = ''
    companyData.value.location = {
      lat: 10.7561542,
      lng: 106.6896784
    }
    return
  }

  geocoder.geocode({ address }, function (results, status) {
    if (status === 'OK') {
      if (results && results.length > 0) {
        const location = results[0].geometry.location
        companyData.value.location = {
          lat: location.lat(),
          lng: location.lng()
        }
        companyData.value.locationName = results[0].formatted_address
      }
    }
  })
}, 500)

const openModalLocation = () => {
  showLocation.value = true
}

const handleSetPosition = (data: any) => {
  companyData.value.locationName = data.formatted_address
  companyData.value.location = {
    lat: data.geometry.location.lat(),
    lng: data.geometry.location.lng()
  }
}

const getLocationMap = (data: any) => {
  const geocoder = new google.maps.Geocoder()
  geocoder.geocode({ location: data }).then((result) => {
    const { results } = result
    companyData.value.locationName = results[0].formatted_address
    companyData.value.location = {
      lat: results[0].geometry.location.lat(),
      lng: results[0].geometry.location.lng()
    }
  })
}

const handleSubmit = async () => {
  const result = await $api($url.company.updateCompanyInfo, { body: companyData.value })
  const data: any = $lodash.get(result, 'data.value')
  const newData = data.data || {}
  if (data.success) {
    store.system.setCompany(newData)
    emits('update', newData)
    $toast().success($t('labels.updateSuccess'))
    close()
  }
}

const close = () => {
  visible.value = false
}

defineExpose({ open, close })
</script>
